import { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import {
  activeFilterCount,
  EMPTY_FILTER,
  isFilterActive,
  presetRange,
  type DatePreset,
  type RecordingFilter,
} from './recordingFilterRules';

/**
 * The filter bar every recording list shares.
 *
 * Each dimension is a searchable dropdown rather than a plain select or a
 * free-text box: with a handful of recordings you can eyeball a list, but a
 * tenant running daily ORs accumulates hundreds of participants and dozens of
 * procedures, and at that size typing a name beats scrolling for it. They are
 * multi-select because the real questions are plural — "Kebebew *and* lexi on
 * this case" — which a single-select forces you to ask twice.
 *
 * Options are supplied by the caller from the rows on screen, so the lists
 * only offer choices that return something.
 *
 * The date control is presets plus an exact from/to. The presets answer the
 * common questions in one click; the exact range exists because "that Tuesday
 * afternoon" is a real way people look for a case, and a day-granular filter
 * can't express it. `datetime-local` is deliberate: it gives a native calendar
 * AND a time, on every modern browser, with no date-picker dependency — MUI's
 * own range picker is a paid Pro component.
 */

export interface RecordingFiltersProps {
  value: RecordingFilter;
  onChange: (next: RecordingFilter) => void;
  options: {
    participants: string[];
    devices: string[];
    procedures: string[];
  };
  /** Hide dimensions a screen has no use for (the participant's own dashboard
   *  has one participant; the device list has no device dimension). */
  show?: {
    participants?: boolean;
    devices?: boolean;
    procedures?: boolean;
    dates?: boolean;
    /** Off by default — only the screens people investigate from need it. */
    processing?: boolean;
  };
  /** Extra controls a screen owns (status, department) sit alongside. */
  children?: React.ReactNode;
  /** Rendered result count, e.g. "12 of 340". */
  summary?: string;
}

/** Case-insensitive, number-aware alphabetical copy ("Room 2" before "Room 10"). */
function alphabetical(xs: string[]): string[] {
  return [...xs].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }),
  );
}

const ALL_SHOWN = {
  participants: true,
  devices: true,
  procedures: true,
  dates: true,
  processing: false,
};

export function RecordingFilters({
  value,
  onChange,
  options,
  show,
  children,
  summary,
}: RecordingFiltersProps) {
  const vis = { ...ALL_SHOWN, ...(show ?? {}) };
  // Callers pass options in whatever order the data arrived; the pickers
  // always list them alphabetically so a name is findable by eye too.
  const participantOptions = useMemo(
    () => alphabetical(options.participants),
    [options.participants],
  );
  const procedureOptions = useMemo(
    () => alphabetical(options.procedures),
    [options.procedures],
  );
  const deviceOptions = useMemo(
    () => alphabetical(options.devices),
    [options.devices],
  );
  // The exact from/to is folded away by default: most lookups are answered by
  // a preset, and an always-open pair of datetime inputs makes the bar look
  // like a form to fill in rather than a shortcut.
  const [showExact, setShowExact] = useState(
    () => !!value.from || !!value.to,
  );
  // Which preset the operator actually clicked, when they clicked one.
  //
  // Needed because two presets can denote the SAME range: on a Monday,
  // "Today" and "This week" are identical (the week starts today), and on the
  // 1st of a month so is "This month". Deriving the highlight from the bounds
  // alone therefore cannot tell them apart, and the first match in the list
  // wins — so clicking "This week" on a Monday lit up "Today" instead, all
  // day. The click is the only thing that knows which was meant.
  const [clicked, setClicked] = useState<DatePreset | null>(null);
  const active = isFilterActive(value);
  const count = activeFilterCount(value);

  const set = (patch: Partial<RecordingFilter>) =>
    onChange({ ...value, ...patch });

  const applyPreset = (p: DatePreset) => {
    const r = presetRange(p);
    setShowExact(false);
    setClicked(p);
    set({ from: r.from, to: r.to });
  };

  /** Which preset (if any) the current bounds correspond to, so the chip shows
   *  as selected after a click — and stops showing if the range is edited. */
  const activePreset = useMemo<DatePreset | null>(() => {
    if (!value.from && !value.to) return 'all';
    // A clicked preset wins for as long as its range still matches the
    // current bounds — that is what disambiguates the overlapping presets
    // above. Editing the range by hand, or arriving on a shared link, leaves
    // `clicked` null or stale, and we fall back to deriving the label from
    // the bounds; there the narrowest sensible match is the right answer.
    if (clicked) {
      const r = presetRange(clicked);
      if (r.from === value.from && r.to === value.to) return clicked;
    }
    for (const p of ['today', 'yesterday', 'week', 'month'] as DatePreset[]) {
      const r = presetRange(p);
      if (r.from === value.from && r.to === value.to) return p;
    }
    return null;
  }, [value.from, value.to, clicked]);

  return (
    // ONE wrapping row, not two stacked ones.
    //
    // Rendering the dates on a row of their own meant a screen with a single
    // dropdown (the dashboards) left a whole line of dead space to the right
    // of it, and a screen with a toggle beside the bar (Activity) had that
    // toggle vertically centred against a two-row block instead of sitting on
    // the top line. Inline, the chips fill the space where there is room and
    // wrap to a second line only where there genuinely isn't — which is what
    // Activity does naturally with five controls.
    //
    // alignItems flex-start so anything a caller renders next to this bar
    // lines up with the FIRST row rather than floating between rows.
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 1.5 }}
    >
      <>
        {vis.participants && (
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={participantOptions}
            value={value.participants}
            onChange={(_, v) => set({ participants: v })}
            renderInput={(p) => <TextField {...p} label="Participant" />}
            sx={{ width: 210 }}
          />
        )}
        {vis.procedures && (
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={procedureOptions}
            value={value.procedures}
            onChange={(_, v) => set({ procedures: v })}
            renderInput={(p) => <TextField {...p} label="Procedure" />}
            sx={{ width: 210 }}
          />
        )}
        {vis.devices && (
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={deviceOptions}
            value={value.devices}
            onChange={(_, v) => set({ devices: v })}
            renderInput={(p) => <TextField {...p} label="Device" />}
            sx={{ width: 150 }}
          />
        )}
        {vis.processing && (
          // A chip, not a third dropdown: this is one question with a yes/no
          // answer, and it is asked rarely. Off means "no opinion" rather
          // than "hide the failures" — a recording that will never score has
          // to stay visible in the default view, since looking normal while
          // being stuck is the bug this came from.
          <Chip
            size="small"
            label="Could not be scored"
            onClick={() =>
              set({
                processingFailed:
                  value.processingFailed === true ? undefined : true,
              })
            }
            color={value.processingFailed === true ? 'error' : 'default'}
            variant={value.processingFailed === true ? 'filled' : 'outlined'}
          />
        )}
        {children}
        {active && (
          <Button size="small" onClick={() => onChange({ ...EMPTY_FILTER })}>
            Clear{count > 1 ? ` (${count})` : ''}
          </Button>
        )}
      </>

      {vis.dates && (
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          {(
            [
              ['all', 'All time'],
              ['today', 'Today'],
              ['yesterday', 'Yesterday'],
              ['week', 'This week'],
              ['month', 'This month'],
            ] as [DatePreset, string][]
          ).map(([p, label]) => (
            <Chip
              key={p}
              size="small"
              label={label}
              onClick={() => applyPreset(p)}
              color={activePreset === p ? 'primary' : 'default'}
              variant={activePreset === p ? 'filled' : 'outlined'}
            />
          ))}
          <Chip
            size="small"
            label="Exact range…"
            onClick={() => setShowExact((v) => !v)}
            color={activePreset === null ? 'primary' : 'default'}
            variant={activePreset === null ? 'filled' : 'outlined'}
          />
          <Collapse in={showExact} orientation="horizontal" unmountOnExit>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
              <TextField
                size="small"
                type="datetime-local"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={value.from}
                onChange={(e) => set({ from: e.target.value })}
                sx={{ minWidth: 210 }}
              />
              <TextField
                size="small"
                type="datetime-local"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={value.to}
                onChange={(e) => set({ to: e.target.value })}
                sx={{ minWidth: 210 }}
              />
            </Stack>
          </Collapse>
        </Stack>
      )}
      {/* Pushed hard right, after everything, so the count sits at the end of
          the bar rather than mid-row. */}
      <Box sx={{ flexGrow: 1 }} />
      {summary && (
        <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
          {summary}
        </Box>
      )}
    </Stack>
  );
}
